<script setup lang="ts">
    import { inject, onMounted, reactive } from 'vue';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { ContextAccess, loadCurrentSubscription } from '@shared/Root/Context.access';
    import Loading from '@shared/components/Loading.vue';
    import Modal from '@shared/components/Modal.vue';
    import List from '@shared/components/List/List.vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { AlertTypes } from '@shared/components/BsAlerts';
    import { formatDate } from '@shared/utils/formatDateTime';
    import { AccountInvoice, getInvoices, getRenderedInvoice } from './AccountInvoices.access';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const contextAccess: ContextAccess = injectorAccess.injector.get(ContextAccess);

    const state = reactive<{
        isLoading: boolean;
        loadError: boolean;
        invoices: AccountInvoice[] | null;
        selectedInvoice: AccountInvoice | null;
        showModal: boolean;
        isLoadingHtml: boolean;
    }>({
        isLoading: true,
        loadError: false,
        invoices: null,
        selectedInvoice: null,
        showModal: false,
        isLoadingHtml: false,
    });

    onMounted(async () => {
        try {
            if (!contextAccess.state.currentSubscription) {
                contextAccess.state.currentSubscription = await loadCurrentSubscription();
            }

            state.invoices = await getInvoices(contextAccess.state.currentSubscription?.ServiceSubscriptionId ?? null, true);
        } catch {
            state.loadError = true;
        } finally {
            state.isLoading = false;
        }
    });

    const filterKeys = ['InvoiceNumber', 'RecipientName'];

    const orderOptions = [
        { key: 'DueDate', display: $Resources.InvoicesTabDueDate },
        { key: 'InvoiceNumber', display: $Resources.InvoicesTabInvoiceNumber },
        { key: 'Amount', display: $Resources.InvoicesTabAmount },
        { key: 'Status', display: $Resources.InvoicesTabStatus },
    ];

    async function select(invoice: AccountInvoice): Promise<void> {
        state.selectedInvoice = invoice;
        state.showModal = true;

        await renderInvoice();
    }

    async function renderInvoice(): Promise<void> {
        replaceHtmlInInvoiceModal('');

        state.isLoadingHtml = true;

        const invoiceHtml = await getRenderedInvoice(
            state.selectedInvoice?.InvoiceNumber + '',
            'Paper',
            contextAccess.state.currentSubscription?.ServiceSubscriptionId ?? null
        ).finally(() => (state.isLoadingHtml = false));

        replaceHtmlInInvoiceModal(invoiceHtml);
    }

    function replaceHtmlInInvoiceModal(invoiceHtml: string): void {
        const iframe = document.getElementById('invoiceIframe') as HTMLIFrameElement | null;
        if (!iframe?.contentWindow) return;

        iframe.contentWindow.document.open();
        iframe.contentWindow.document.write(invoiceHtml);
        iframe.contentWindow.document.close();
    }

    function printInvoice(): void {
        const iframe = document.getElementById('invoiceIframe') as HTMLIFrameElement | null;
        iframe?.contentWindow?.print();
    }

    function modalClosed(): void {
        state.showModal = false;
    }
</script>

<template>
    <h1 class="h2 m-lg-b">{{ $Resources.InvoicesTab }}</h1>

    <Loading :show="state.isLoading" loadingStyle="progress-bar" fixed></Loading>

    <BsAlert v-if="state.loadError" :type="AlertTypes.Danger" :dismissible="false" :title="$Resources.ErrorHeading" :content="$Resources.ErrorContactSupport" />

    <List
        v-if="!state.isLoading && !state.loadError"
        itemId="InvoiceId"
        displayAs="table"
        :items="state.invoices"
        :filterKeys="filterKeys"
        :orderOptions="orderOptions"
        orderByValue="DueDate"
        orderByDirection="desc"
        :tableHeaders="[
            $Resources.InvoicesTabInvoiceNumber,
            $Resources.InvoicesTabRecipient,
            $Resources.InvoicesTabStatus,
            $Resources.InvoicesTabAmount,
            $Resources.InvoicesTabDueDate,
            $Resources.InvoicesTabPaidDate,
            $Resources.ShowLabel,
        ]">
        <template #item="{ item: invoice }">
            <td>
                {{ invoice.InvoiceNumber }}
            </td>
            <td>{{ invoice.RecipientName }}</td>
            <td>{{ invoice.Status }}</td>
            <td>{{ invoice.Amount }} {{ invoice.CurrencyCode }}</td>
            <td>{{ formatDate(invoice.DueDate) }}</td>
            <td>{{ formatDate(invoice.PaidDate) }}</td>
            <td>
                <button type="button" class="btn btn-default btn-xs" @click.prevent="select(invoice)">
                    {{ $Resources.ShowLabel }}
                </button>
            </td>
        </template>
    </List>

    <Modal size="lg" :title="state.selectedInvoice?.InvoiceNumber + ''" :show="state.showModal" @close="modalClosed" :closeOnClickOutside="true">
        <template #content>
            <Loading :show="state.isLoadingHtml"></Loading>
            <iframe id="invoiceIframe" v-show="!state.isLoadingHtml"></iframe>
        </template>
        <template #button>
            <button type="button" class="btn btn-primary" @click="printInvoice()" :disabled="state.isLoadingHtml">
                {{ $Resources.PrintButton }}
            </button>
        </template>
    </Modal>
</template>

<style scoped>
    #invoiceIframe {
        width: 100%;
        border: 1px solid lightgray;
        height: 800px;
    }
</style>
